import Swiper from 'swiper';
import {
  A11y,
  FreeMode,
  Navigation,
  Pagination,
  Scrollbar,
  Mousewheel,
} from 'swiper/modules';
import { __, sprintf, setLocaleData } from '@wordpress/i18n';
import uniqueId from 'a11y/lib/uniqueId';

/**
 * Carousel component.
 *
 * @param {NodeList}    slides          The carousel list items.
 * @param {HTMLElement} controlsWrapper The wrapper within which
 *   the carousel controls will be appended.
 */
export default class Carousel {
  /**
   * Class constructor
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    // Register text domain.
    setLocaleData({ '': {} }, 'sciencenews');

    const { controlsWrapper, slides } = this.children;
    const { moduleStyles } = this.options;
    Object.assign(this, { controlsWrapper, slides, moduleStyles });

    /**
     * The total number of slides in the carousel.
     * @type {Number}
     */
    this.slideCount = this.slides.length;

    // Bind class methods
    this.createControl = this.createControl.bind(this);
    this.addCarouselControls = this.addCarouselControls.bind(this);
    this.updateIndicators = this.updateIndicators.bind(this);
    this.completeCarouselSetup = this.completeCarouselSetup.bind(this);

    this.init();
  }

  /**
   * Initialize the carousel.
   */
  init() {
    const {
      perPage: perPageOption,
      draggable,
      loop,
    } = this.options;
    const { perPage: perPageAttr } = this.element.dataset;

    let perPage = 1;

    // Configure pagination.
    const { showControls = true } = this.options;
    const { showIndicators = false } = this.options;
    const { showScrollbar = false } = this.options;

    const pagination = showIndicators && this.children.indicatorsWrapper
      ? {
        el: this.children.indicatorsWrapper,
        bulletClass: this.moduleStyles.indicator,
        bulletActiveClass: this.moduleStyles.indicatorActive,
      } : false;

    const scrollbar = showScrollbar && this.children.scrollbarWrapper
      ? {
        el: this.children.scrollbarWrapper,
        draggable: true,
        hide: true,
      } : false;

    // If both options and attribute are undefined, use the default perPage value.
    if (undefined !== perPageOption || undefined !== perPageAttr) {
      // Use options.perPage unless this.element has a `data-per-page` attribute.
      perPage = (undefined === perPageAttr)
        ? perPageOption
        : JSON.parse(perPageAttr);
    }

    // Configure breakpoints.
    const breakpoints = {};
    if (typeof perPage === 'object') {
      Object.keys(perPage).forEach((key) => {
        breakpoints[key] = {
          slidesPerView: perPage[key],
          slidesPerGroup: showIndicators ? this.options.perPage[key] : 1,
        };
      });
    }

    // Initialize Swiper.
    if (this.slideCount > 1) {
      this.carousel = new Swiper(
        this.children.swiper,
        {
          breakpoints,
          draggable,
          duration: 300,
          loop,
          on: {
            beforeInit: showControls ? this.addCarouselControls : null,
            slideChange: this.updateIndicators,
          },
          slidesPerGroup: 1,
          slidesPerView: typeof perPage === 'number' ? perPage : 1,
          spaceBetween: 20,
          modules: [
            A11y,
            FreeMode,
            Mousewheel,
            Navigation,
            Pagination,
            Scrollbar,
          ],
          a11y: {
            containerRole: '',
            slideRole: false,
          },
          navigation: {
            nextEl: '.swiper__button-next',
            prevEl: '.swiper__button-previous',
          },
          pagination,
          scrollbar,
          freeMode: {
            enabled: showScrollbar,
            sticky: true,
          },
          mousewheel: {
            enabled: showScrollbar,
            forceToAxis: true, // horizontal only
            sensitivity: 1,
          },
        },
      );

      // Tie a bow on it.
      this.completeCarouselSetup();
    }
  }

  /**
   * Create a list element contining a control button.
   *
   * @param {String} context The button context (next/previous).
   *
   * @return {HTMLElement} <li><button></li>.
   */
  createControl(context) {
    const { icons: { [context]: icon } } = this.options || {};
    const li = document.createElement('li');
    li.classList.add(this.moduleStyles[context]);

    const button = document.createElement('button');
    button.id = uniqueId();

    const ariaLabel = sprintf(__('%s slide', 'sciencenews'), context);
    button.setAttribute('aria-label', ariaLabel);
    button.classList.add(`swiper__button-${context}`);

    // Create a document fragment from the SVG icon string.
    const range = document.createRange();
    const fragment = range.createContextualFragment(icon);

    // Add the button icon
    button.appendChild(fragment);

    li.appendChild(button);

    return li;
  }

  /**
   * Finishes carousel per-page and attribute setup.
   * Must be run after onInit()
   */
  completeCarouselSetup() {
    this.element.setAttribute('role', 'region');
    this.element.tabIndex = 0;
  }

  /**
   * Add the carousel controls.
   */
  addCarouselControls() {
    const { controlsWrapper } = this.children;
    this.element.classList.add('initialized');

    const controls = document.createElement('ul');
    const ariaLabel = __('Carousel controls', 'sciencenews');
    controls.setAttribute('aria-label', ariaLabel);
    controls.classList.add(this.moduleStyles.controls);

    // Add the 'previous' button .
    const previous = this.createControl('previous');
    this.prev = previous.querySelector('button');
    // Previous is disabled until there's a slide to which we can navigate.
    this.prev.setAttribute('aria-disabled', 'true');
    controls.appendChild(previous); // append the <li>

    // Add the 'next' button
    const next = this.createControl('next');
    this.next = next.querySelector('button');
    controls.appendChild(next); // append the <li>

    controlsWrapper.appendChild(controls); // append the <li>
  }

  /**
   * Updates slide indicators for the current slide.
   */
  updateIndicators() {
    const currentSlide = this?.carousel?.realIndex || 0;
    const indicatorWrapper = this.element
      .querySelector(`.${this.moduleStyles.indicators}`);

    if (indicatorWrapper !== null) {
      const indicators = Array.from(indicatorWrapper.children);

      for (let i = 0; i < indicators.length; i += 1) {
        if (indicators[i].classList.contains(
          this.moduleStyles.indicatorActive,
        )) {
          indicators[i].classList.remove(this.moduleStyles.indicatorActive);
        }

        if (i === currentSlide) {
          indicators[i].classList.add(this.moduleStyles.indicatorActive);
        }
      }
    }
  }
}
